import {useEffect, useState} from 'react'
import {useQuery} from 'react-query'
import {globalSearch, getSearchCategories} from './_requests'
import {SearchResponse} from './_models'

export const MIN_SEARCH_LENGTH = 2

const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

/**
 * Hook membaca kategori yang diizinkan untuk user saat ini dari backend/database.
 */
export const useSearchCategories = (enabled = true) => {
  return useQuery<string[]>(
    ['global-search-categories'],
    ({signal}) => getSearchCategories(signal),
    {
      enabled,
      staleTime: 60_000,
      retry: false,
      refetchOnWindowFocus: false,
    }
  )
}

/**
 * Hook Global Search: debounce 250ms, minimal 2 karakter, cache 30 detik,
 * request lama otomatis dibatalkan (AbortSignal) saat user terus mengetik.
 */
export const useGlobalSearch = (query: string, category: string, enabled = true) => {
  const term = useDebouncedValue(query.trim(), 250)
  const shouldFetch = enabled && term.length >= MIN_SEARCH_LENGTH && category !== 'Pages'

  const result = useQuery<SearchResponse>(
    ['global-search', term, category],
    ({signal}) => globalSearch(term, category, 5, signal),
    {
      enabled: shouldFetch,
      keepPreviousData: true,
      staleTime: 30_000,
      retry: false,
      refetchOnWindowFocus: false,
    }
  )

  return {
    ...result,
    term,
    isDebouncing: query.trim() !== term,
    shouldFetch,
  }
}
